import { Col, Form, Row, Space, message } from "antd";
import dayjs from "dayjs";
import React, { useEffect, useRef, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { Calendar } from "../../../../../assets/svg";
import { useMasterConfigSelect } from "../../../../../hooks/useMasterConfigSelect";
import { Booking, UsageType } from "../../../../../types/bookings";
import { SelectOption } from "../../../../../types/select";
import {
  DateField,
  InputCheckbox,
  InputField,
  RangePickerField,
  ReactSelectField,
} from "../../../../atoms/FormElement";
import { findSelectOption } from "../../../../libs/form";
import { DATE_FORMATS, formatDateByType, getRentFreePeriodValue } from "../../../../libs/utils";
import Button from "../../../../molecules/Button";
import { getBookingDetail, getLeaseTerms, getTenantDetailByEmail, updateBooking } from "../../ducks/actions";
import { useGetLeaseDuration } from "../../hooks/useGetLeaseDuration";
import { torRulesOption } from "../../utils";
import {
  dateBookingDDMMYYFormat,
  disableBookingPeriodDates,
  disableFitoutPeriodDates,
  disableLeasePeriodDates,
  disableMoveInDate,
  filterDropdownValue,
  filterPayload,
  rangePickerInitialValue,
  rentFreePeriodValues,
  validateEmail,
} from "../utils";
import { Loading } from "./index";

const rentFreePeriodOptions = rentFreePeriodValues() as SelectOption[];

interface IProps {
  renewal?: boolean;
  parentState: (obj: object) => void;
}

let initialState = {
  visible: false,
  authorizedSignatory: "",
};

const index = (props: IProps) => {
  // hooks initialization
  const dispatch: any = useDispatch();
  const { leaseId = "" } = useParams();
  const [termsandConditions, setTermsandConditions] = useState([]);
  const [termsandConditionsId, setTermsandConditionsId] = useState({});

  const initialLeaseEndDateRef = useRef<dayjs.Dayjs>();

  // redux states
  const { bookingForm, landlordUsers, bookingDetails } = useSelector((state: any) => state.newBooking);
  const { leaseTerms } = (bookingDetails || {}) as Booking;
  const { leaseStartDate, leaseEndDate, rentFreeStartDate, rentFreeEndDate } = leaseTerms;
  let torExist = torRulesOption?.find((item) => item?.value === (bookingDetails?.torType ?? bookingForm?.torType));

  const {
    control,
    setValue,
    getValues,
    handleSubmit,
    clearErrors,
    setError,
    formState: { errors },
  } = useForm();

  const email = useWatch({ control, name: "email" });

  const usagesType = bookingDetails?.propertyDetails?.usagesType;
  // states
  const [{ visible }, setState] = useState(initialState);

  const updateState = (state: {}) => setState((prevState: any) => ({ ...prevState, ...state }));
  const { label } = useGetLeaseDuration(
    bookingDetails?.parentLeaseTerms?.leaseStartDate ?? bookingDetails?.leaseTerms?.leaseStartDate,
    bookingDetails?.parentLeaseTerms?.leaseEndDate ?? bookingDetails?.leaseTerms?.leaseEndDate
  );

  useEffect(() => {
    (async () => {
      try {
        const response = await dispatch(
          getLeaseTerms(bookingDetails.propertyId, bookingDetails?.propertyDetails?.usagesType)
        );
        const term = response.map((term) => ({
          value: term._id,
          label: term.templateName,
        }));
        setTermsandConditions(term);
        setTermsandConditionsId({
          value: bookingDetails.termsConditionId,
          label: term.find((t) => t.value === bookingDetails.termsConditionId)?.label,
        });
      } catch (e) {}
    })();
  }, [bookingDetails, bookingForm]);

  useEffect(() => {
    if (validateEmail(email) && !bookingForm?.email) dispatch(getTenantDetailByEmail(email));
  }, [email]);

  const { selectOptions: leaseNatureOptions, isSuccessMasterConfig } = useMasterConfigSelect({
    configItem: "leaseNature",
    filterBy: "usageType",
    value: String(usagesType).toLowerCase() as UsageType,
    isEnabled: Boolean(usagesType),
  });

  useEffect(() => {
    if (!bookingForm?.leaseTerms?.leaseNature || !isSuccessMasterConfig) return;

    const selectedLeaseNature = findSelectOption(leaseNatureOptions, bookingForm.leaseTerms.leaseNature);
    if (!selectedLeaseNature) return;

    setValue("leaseNature", selectedLeaseNature);
  }, [bookingForm?.leaseTerms?.leaseNature, isSuccessMasterConfig]);

  useEffect(() => {
    const rentFreePeriodValue = getRentFreePeriodValue(bookingDetails?.leaseTerms);

    const selectedRentFreePeriod = findSelectOption(rentFreePeriodOptions, rentFreePeriodValue);
    if (!selectedRentFreePeriod) return;

    setValue("rentFreePeriod", selectedRentFreePeriod);
  }, [bookingDetails?.leaseTerms?.rentFreePeriod, rentFreePeriodOptions]);

  useEffect(() => {
    initialLeaseEndDateRef.current =
      rentFreeStartDate || leaseEndDate ? dayjs(rentFreeStartDate ?? leaseEndDate) : null;
  }, [leaseEndDate, rentFreeStartDate]);

  const onSubmit = async (data: any, type) => {
    clearErrors();
    updateState({ visible: true });
    let dataCopy = { ...(data || getValues()) };
    if (dataCopy.numberOfOcupants && !/^\d+$/.test(dataCopy.numberOfOcupants)) {
      setError("numberOfOcupants", {
        type: "manual",
        message: "Please enter valid number of ocupants",
      });
      return;
    }

    updateState({ visible: true });

    const commercialFields =
      usagesType == "Commercial" && dataCopy?.fitOutPeriod
        ? {
            fitOutEndDate: formatDateByType(dataCopy?.fitOutPeriod[1], "api"),
            fitOutStartDate: formatDateByType(dataCopy?.fitOutPeriod[0], "api"),
          }
        : {}; //This code added for diff payload: comm, resi

    if (usagesType === "Commercial" && bookingDetails?.leaseTerms) {
      const { noOfCheques, contractAmount, securityDeposit } = bookingDetails.leaseTerms;

      if (noOfCheques) {
        commercialFields["noOfCheques"] = noOfCheques;
      }

      if (contractAmount) {
        commercialFields["contractAmount"] = contractAmount;
      }

      if (securityDeposit) {
        commercialFields["securityDeposit"] = securityDeposit;
      }
    }

    const residentialFields = usagesType != "Commercial" ? { numberOfOcupants: dataCopy?.numberOfOcupants } : {};

    const rentFreePeriodValue = Number(dataCopy?.rentFreePeriod?.value);

    const hasRentFreePeriod =
      isFinite(rentFreePeriodValue) &&
      rentFreePeriodValue > 0 &&
      initialLeaseEndDateRef.current &&
      dataCopy?.leaseTermPeriod?.[1];

    const rentFreePeriod = hasRentFreePeriod
      ? {
          rentFreeStartDate: formatDateByType(initialLeaseEndDateRef.current, "api"),
          rentFreeEndDate: formatDateByType(dataCopy.leaseTermPeriod[1], "api"),
        }
      : {};

    let body = {
      leaseManager: dataCopy.leaseManager.value,
      ...residentialFields,
      externalAgent: dataCopy.externalAgent,
      termsConditionId: dataCopy?.termsConditionId?.value,
      leaseTerms: {
        leaseType: "new",
        leaseNature: dataCopy.leaseNature.value,
        moveInDate: formatDateByType(dataCopy.moveInDate, "api"),
        leaseEndDate: formatDateByType(dataCopy.leaseTermPeriod && dataCopy.leaseTermPeriod[1], "api"),
        leaseStartDate: formatDateByType(dataCopy.leaseTermPeriod && dataCopy.leaseTermPeriod[0], "api"),
        noOfFreeParkingSlots: dataCopy?.noOfFreeParkingSlots === "" ? 0 : Number(dataCopy?.noOfFreeParkingSlots),
        commencementDate: formatDateByType(dataCopy?.commencementDate, "api"),
        ...rentFreePeriod,
        ...commercialFields,
      },
      bookingTerms: {
        ...bookingForm?.bookingTerms,
        bookingEndDate: formatDateByType(dataCopy.bookingPeriod && dataCopy.bookingPeriod[1], "iso"),
        bookingStartDate: formatDateByType(dataCopy.bookingPeriod && dataCopy.bookingPeriod[0], "iso"),
      },
    };

    //FOR TASK 8442
    if (body?.leaseTerms?.moveInDate) body.leaseTerms["moveInDateConfirm"] = dataCopy.moveInDateConfirm ? "yes" : "no";

    if (dataCopy?.torRuleType?.value && usagesType === "Commercial") {
      body["torType"] = dataCopy?.torRuleType?.value;
    }

    try {
      const response = await dispatch(updateBooking({ leaseId, body: filterPayload(body) }));

      setTimeout(() => {
        dispatch(getBookingDetail(leaseId));
      }, 400);

      message.success(response.data.message);
      updateState({ visible: false });
      props.parentState({
        modalPayload: { iKey: "", title: "", extraText: "", open: false },
      });
    } catch (error) {
      updateState({ visible: false });
      props.parentState({ visible: false });
      message.error(error.response?.data?.error || error.response?.data?.message);
    }
  };

  return (
    <Form layout="vertical" name="bookingform" scrollToFirstError className="bf-main" onFinish={handleSubmit(onSubmit)}>
      <Loading visible={visible} />

      <Col span={24}>
        <Row gutter={24}>
          <Col span={8}>
            <ReactSelectField
              control={control}
              isRequired={true}
              setValue={setValue}
              label="Lease Executive"
              fieldname="leaseManager"
              selectOption={landlordUsers}
              iProps={{ placeholder: "Select" }}
              rules={{ required: "Please select one" }}
              validate={errors.leaseManager && "error"}
              validMessage={errors.leaseManager && errors.leaseManager.message}
              valueGot={filterDropdownValue(
                bookingForm?.leaseManager?.userId || bookingForm?.leaseManager,
                landlordUsers
              )}
            />
          </Col>

          <Col span={8}>
            <InputField
              control={control}
              setValue={setValue}
              label="External Agent"
              fieldname="externalAgent"
              valueGot={bookingDetails?.externalAgent || "empty"}
              validate={errors.externalAgent && "error"}
              validMessage={errors.externalAgent && errors.externalAgent.message}
            />
          </Col>

          <Col span={8}>
            <ReactSelectField
              isRequired={true}
              control={control}
              setValue={setValue}
              label="Lease Nature"
              fieldname="leaseNature"
              selectOption={leaseNatureOptions}
              iProps={{ placeholder: "Select" }}
              validate={errors.leaseNature && "error"}
              rules={{ required: "Please select lease nature" }}
              validMessage={errors.leaseNature && errors.leaseNature.message}
            />
          </Col>

          {/* next row */}
          {usagesType != "Commercial" && (
            <Col span={8}>
              <InputField
                initValue={1}
                control={control}
                isRequired={true}
                setValue={setValue}
                label="No. of Occupants"
                fieldname="numberOfOcupants"
                valueGot={bookingForm?.numberOfOcupants}
                validate={errors.numberOfOcupants && "error"}
                rules={{ required: "Please enter occupants" }}
                validMessage={errors.numberOfOcupants && errors.numberOfOcupants.message}
              />
            </Col>
          )}

          <Col span={8}>
            <ReactSelectField
              control={control}
              setValue={setValue}
              label="Terms & Conditions"
              selectOption={termsandConditions}
              fieldname="termsConditionId"
              iProps={{
                placeholder: "Select",
                addonAfter: <Calendar />,
                suffixIcon: <Calendar />,
              }}
              validate={errors.termsAndConditions && "error"}
              validMessage={errors.termsAndConditions && errors.termsAndConditions.message}
              valueGot={termsandConditionsId}
            />
          </Col>

          {!props.renewal && (
            <Col span={8}>
              <RangePickerField
                isRequired={true}
                control={control}
                setValue={setValue}
                label="Booking Period"
                fieldname="bookingPeriod"
                validate={errors.dateRange && "error"}
                initValue={rangePickerInitialValue(bookingDetails, "booking_period")}
                onDateChange={(date) => {
                  setValue("bookingPeriod", [null, null]);
                  setValue("bookingPeriod", [date[0], dayjs(date[0]).add(14, "days")]);
                  if (date && date[0] && usagesType.toLowerCase() !== "commercial") {
                    setValue("leaseTermPeriod", [
                      dayjs(date[0]), //.add(14, "days") removed as per feedback vish
                      dayjs(date[0]).add(1, "year").subtract(1, "day"), //.add(14, "days") removed as per feedback vish
                    ]);
                    setValue("moveInDate", dayjs(date[0])); //.add(14, "days") removed as per feedback vish
                  }
                }}
                iProps={{
                  suffixIcon: <Calendar />,
                  format: DATE_FORMATS.display,
                  placeholder: ["Start Date", "End Date"],
                  disabledDate: (date: any) => disableBookingPeriodDates(date, getValues("bookingPeriod")),
                  // onCalendarChange: (date: any) => {
                  //   if (date?.length > 1) {
                  //     const startDate = dayjs(date[1]);
                  //     const endDate = dayjs(date[1]).add(1, 'year')
                  //     const rentStartDate = dayjs(endDate).add(1, 'day')
                  //     const rentEndDate = dayjs(rentStartDate).add(3, 'months')
                  //     if(startDate) {
                  //       setDisableTermDates([startDate, endDate])
                  //       setValue("leaseTermPeriod", [startDate, endDate]);
                  //       setValue("rentFreePeriod", [rentStartDate, rentEndDate]);
                  //     }
                  //   }
                  // },
                }}
                validMessage={errors.bookingPeriod && errors.bookingPeriod.message}
              />
            </Col>
          )}

          {usagesType == "Commercial" && !props.renewal && (
            <Col span={8}>
              <RangePickerField
                control={control}
                setValue={setValue}
                label="Fit-Out Period"
                fieldname="fitOutPeriod"
                validate={errors.fitOutPeriod && "error"}
                onDateChange={(date) => {
                  if (date && date[1]) {
                    setValue("leaseTermPeriod", [dayjs(date[1]).add(1, "day"), dayjs(date[1]).add(1, "year")]);
                    setValue("moveInDate", dayjs(date[1]).add(1, "day"));
                  }
                  setValue("rentFreePeriod", null);
                }}
                initValue={rangePickerInitialValue(bookingDetails, "fitout_period")}
                iProps={{
                  suffixIcon: <Calendar />,
                  format: DATE_FORMATS.display,
                  placeholder: ["Start Date", "End Date"],
                  disabledDate: (date: any) => disableFitoutPeriodDates(date, getValues("bookingPeriod")),
                }}
              />
            </Col>
          )}

          <Col span={8}>
            <RangePickerField
              isRequired={true}
              control={control}
              label="Lease Term"
              setValue={setValue}
              fieldname="leaseTermPeriod"
              validate={errors.leaseTermPeriod && "error"}
              initValue={rangePickerInitialValue(bookingDetails, "lease_period")}
              onDateChange={(date) => {
                // setValue("leaseTermPeriod", [null, null]);
                if (date) {
                  let endDate = dayjs(date[0]).add(1, "year").subtract(1, "day");
                  setTimeout(() => {
                    setValue("leaseTermPeriod", [date[0], endDate]);
                    date[0] && setValue("commencementDate", date[0]);
                  }, 500);
                }
                // else
                //   setLeaseEndDateFun(date[1])
                date && setValue("moveInDate", date[0]);
                setValue("rentFreePeriod", null);
              }}
              // onChange={(date) => {
              //   console.log('hit..2', apiDateFormat(date[0]), apiDateFormat(date[1]));
              //   let endDate = dayjs(date[0]).add(1, "year").subtract(1, "day");
              //   setValue("leaseTermPeriod", [
              //     date[0],
              //     endDate
              //   ]);
              //   // if(date && date[1]) {
              //   //   setLeaseEndDateFun(date[1])
              //   //   initialLeaseEndDate.current = date[1]
              //   // }
              //   setValue("moveInDate", date[0]);
              //   setValue("rentFreePeriod", null);
              // }}
              iProps={{
                suffixIcon: <Calendar />,
                format: DATE_FORMATS.display,
                placeholder: ["Start Date", "End Date"],
                //   disabledDate: (date: any) => disableDate(date),
                // onCalendarChange: (date: any) => {
                //   console.log("date------>",date[0],"date2------->",date[1]);
                //   const startDate = dayjs(date[0]).isValid() ? dayjs(date[0]) : "";
                //   const endDate = dayjs(date[1]).isValid() ? dayjs(date[1]) : "";
                //   // const rentStartDate = dayjs(endDate).add(1, 'day')
                //   // const rentEndDate = dayjs(rentStartDate).add(3, 'months')
                //   setValue("leaseTermPeriod", [startDate, endDate]);
                //   // setValue("rentFreePeriod", [rentStartDate, rentEndDate]);
                // },
                // onChange: (date: any) => {},
                disabledDate: (date: any) =>
                  disableLeasePeriodDates(date, getValues("leaseTermPeriod"), getValues("bookingPeriod")),
              }}
              validMessage={errors.leaseTermPeriod && errors.leaseTermPeriod.message}
            />
          </Col>

          {usagesType == "Commercial" && (
            <Col span={8}>
              <InputField
                label="Lease Duration"
                fieldname={`leaseDuration`}
                control={control}
                setValue={setValue}
                disabled={true}
                initValue={label}
                // valueGot={bookingDetails?.leaseTerms?.noOfFreeParkingSlots}
                iProps={{ readOnly: true, type: "text" }}
              />
            </Col>
          )}

          {/* next row */}
          {!props.renewal && (
            <Col span={8}>
              <ReactSelectField
                control={control}
                setValue={setValue}
                label="Rent Free Period"
                fieldname="rentFreePeriod"
                selectOption={rentFreePeriodOptions}
                onChange={(e) => {
                  const rentFreeStartDate = initialLeaseEndDateRef.current;
                  const rentFreeDays = parseInt(e?.value ?? "0", 10);

                  if (!leaseStartDate || !rentFreeStartDate || isNaN(rentFreeDays)) return;

                  const updatedStartDate = dayjs(leaseStartDate);
                  const updatedEndDate = dayjs(rentFreeStartDate).add(rentFreeDays, "days");
                  const result = [updatedStartDate, updatedEndDate];

                  setValue("leaseTermPeriod", result);
                }}
                iProps={{ placeholder: "Select" }}
              />
            </Col>
            // <Col span={8}>
            //   <RangePickerField
            //     control={control}
            //     setValue={setValue}
            //     label="Rent Free Period"
            //     fieldname="rentFreePeriod"
            //     validate={errors.rentFreePeriod && "error"}
            //     initValue={rangePickerInitialValue(
            //       bookingDetails,
            //       "rent_period"
            //     )}
            //     onChange={(date) => {
            //       if (date)
            //         setValue("leaseTermPeriod", [
            //           getValues("leaseTermPeriod")[0],
            //           date[1],
            //         ]);
            //     }}
            //     iProps={{
            //       suffixIcon: <Calendar />,
            //       format: datePlaceholderddmmyyFormat,
            //       placeholder: ["Start Date", "End Date"],
            //       disabledDate: (date: any) =>
            //         disableRentPeriodDates(date, getValues("leaseTermPeriod")),
            //     }}
            //     validMessage={
            //       errors.rentFreePeriod && errors.rentFreePeriod.message
            //     }
            //   />
            // </Col>
          )}

          {/* Need confirmation from usama balu for this field: removed for now */}
          {/* {!props.renewal && (
            <Col span={8}>
              <RangePickerField
                isRequired={true}
                control={control}
                setValue={setValue}
                label="Rent Free Period"
                fieldname="rentFreePeriod"
                validate={errors.rentFreePeriod && "error"}
                initValue={rangePickerInitialValue(bookingDetails, "rent_period")}
                iProps={{
                  suffixIcon: <Calendar />,
                  placeholder: ["Start Date", "End Date"],
                  onCalendarChange: (date: any) => {
                    const startDate = dayjs(date[0]);
                    const endDate = dayjs(date[1])
                    setValue("rentFreePeriod", [startDate, endDate]);
                  },
                  disabledDate: (date: any) =>
                  disableRentPeriodDates(date, getValues("leaseTermPeriod")),
                }}
                validMessage={
                  errors.rentFreePeriod && errors.rentFreePeriod.message
                }
              />
            </Col>
          )} */}
          <Col span={8} style={{ position: "relative" }}>
            <div style={{ position: "absolute", right: "14px", top: "-1px" }}>
              <InputCheckbox
                fieldname="moveInDateConfirm"
                label="Confirmed"
                control={control}
                classes={`prm-checkbox`}
                iProps={{
                  className: `prm-checkbox`,
                }}
                valueGot={bookingForm?.leaseTerms?.moveInDateConfirm == "yes" ? true : ""}
                setValue={setValue}
                // validate={errors.confirmed && "error"}
                // validMessage={errors.confirmed && errors.confirmed.message}
                initValue=""
                // rules={{ required: "Should accept agreement" }}
              />
            </div>
            <DateField
              control={control}
              isRequired={true}
              setValue={setValue}
              label={usagesType === "Commercial" ? "Move-In / Handover Date" : "Move-in Date"}
              fieldname="moveInDate"
              rules={{ required: "Enter Date" }}
              validate={errors?.moveInDate && "error"}
              valueGot={dateBookingDDMMYYFormat(bookingForm?.leaseTerms?.moveInDate)}
              validMessage={errors?.moveInDate && errors?.moveInDate.message}
              iProps={{
                format: DATE_FORMATS.display,
                placeholder: DATE_FORMATS.display,
                disabledDate: (date) =>
                  disableMoveInDate(date, getValues("leaseTermPeriod")[0], getValues("bookingPeriod")[0]),
              }}
            />
          </Col>

            <Col span={8}>
              <DateField
                control={control}
                isRequired={true}
                setValue={setValue}
                label={"Rent Commencement Date"}
                fieldname="commencementDate"
                rules={{ required: "Enter Date" }}
                validate={errors?.commencementDate && "error"}
                valueGot={dateBookingDDMMYYFormat(
                  bookingForm?.leaseTerms?.commencementDate || bookingDetails?.leaseTerms?.leaseStartDate
                )}
                validMessage={errors?.commencementDate && errors?.commencementDate.message}
                iProps={{
                  format: DATE_FORMATS.display,
                  placeholder: DATE_FORMATS.display,
                  // disabledDate: (date) =>
                  //   disableMoveInDate(date, getValues("leaseTermPeriod")[0], getValues("bookingPeriod")[0]),
                }}
              />
            </Col>

          {usagesType == "Commercial" && (
            <Col span={8}>
              <ReactSelectField
                control={control}
                isRequired={false}
                setValue={setValue}
                label="Lease Type"
                fieldname="torRuleType"
                selectOption={torRulesOption}
                valueGot={torExist ? torExist : torRulesOption[0]}
                // onChange={(e) => localStorage?.setItem("torType", e?.value)}
                iProps={{ placeholder: "Select" }}
              />
            </Col>
          )}

          <Col span={8}>
            <InputField
              label="Number of parking slots"
              fieldname={`noOfFreeParkingSlots`}
              control={control}
              setValue={setValue}
              initValue={0}
              valueGot={bookingDetails?.leaseTerms?.noOfFreeParkingSlots}
              iProps={{ readOnly: false, min: 0, type: "number" }}
            />
          </Col>
        </Row>

        <Row gutter={24} className="footer">
          <Space size="middle">
            <Button
              label="Cancel"
              className="bf-discard-button"
              onClick={() => props.parentState({ modalPayload: { open: false } })}
            />

            <Button type="submit" label="Update" className="cc-fotter_next-button" />
          </Space>
        </Row>
      </Col>
    </Form>
  );
};

export default index;
